/**
 * ══════════════════════════════════════════════════════════════════════
 * NETUNO DESIGN SYSTEM — Risk Color Tokens (Centralized)
 * ══════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for risk/criticality colors across all modules.
 * Uses CSS custom properties from index.css for theme consistency.
 *
 * Usage:
 *   import { riskColor, riskBgColor, riskBorderColor, RISK_LABELS } from '@/lib/riskTokens';
 *   style={{ color: riskColor('critico') }}
 *   className={riskTailwindBg('baixo')}
 */

// ─── HSL color strings for inline styles (charts, SVG, dynamic) ──────

const RISK_HSL: Record<string, string> = {
  critico: 'hsl(var(--destructive))',
  alto: 'hsl(var(--warning))',
  moderado: 'hsl(var(--accent))',
  baixo: 'hsl(var(--success))',
};

const RISK_HSL_BG: Record<string, string> = {
  critico: 'hsl(var(--destructive) / 0.15)',
  alto: 'hsl(var(--warning) / 0.15)',
  moderado: 'hsl(var(--accent) / 0.15)',
  baixo: 'hsl(var(--success) / 0.15)',
};

const RISK_HSL_BORDER: Record<string, string> = {
  critico: 'hsl(var(--destructive) / 0.4)',
  alto: 'hsl(var(--warning) / 0.4)',
  moderado: 'hsl(var(--accent) / 0.4)',
  baixo: 'hsl(var(--success) / 0.4)',
};

/** Returns the solid HSL color for a risk level (for inline styles / charts). */
export function riskColor(level: string | null | undefined): string {
  return RISK_HSL[(level || '').toLowerCase()] || 'hsl(var(--muted-foreground))';
}

/** Returns a 15% opacity background HSL for a risk level. */
export function riskBgColor(level: string | null | undefined): string {
  return RISK_HSL_BG[(level || '').toLowerCase()] || 'hsl(var(--muted) / 0.3)';
}

/** Returns a 40% opacity border HSL for a risk level. */
export function riskBorderColor(level: string | null | undefined): string {
  return RISK_HSL_BORDER[(level || '').toLowerCase()] || 'hsl(var(--border))';
}

// ─── Tailwind class helpers (for className usage) ────────────────────

const RISK_TW_TEXT: Record<string, string> = {
  critico: 'text-destructive',
  alto: 'text-warning',
  moderado: 'text-accent',
  baixo: 'text-success',
};

const RISK_TW_BG: Record<string, string> = {
  critico: 'bg-destructive',
  alto: 'bg-warning',
  moderado: 'bg-accent',
  baixo: 'bg-success',
};

/** Returns a Tailwind text-color class for a risk level. */
export function riskTailwindText(level: string | null | undefined): string {
  return RISK_TW_TEXT[(level || '').toLowerCase()] || 'text-muted-foreground';
}

/** Returns a Tailwind bg-color class for a risk level. */
export function riskTailwindBg(level: string | null | undefined): string {
  return RISK_TW_BG[(level || '').toLowerCase()] || 'bg-muted';
}

// ─── Labels ──────────────────────────────────────────────────────────

export const RISK_LABELS: Record<string, string> = {
  critico: 'Crítico',
  alto: 'Alto',
  moderado: 'Moderado',
  baixo: 'Baixo',
};

export function riskLabel(level: string | null | undefined): string {
  return RISK_LABELS[(level || '').toLowerCase()] || (level || '—');
}

// ─── Score-to-level mapping ──────────────────────────────────────────

/** Derives a risk level string from a numeric score (0-100). */
export function riskLevelFromScore(score: number | null | undefined): string {
  if (score == null) return 'baixo';
  if (score >= 80) return 'critico';
  if (score >= 60) return 'alto';
  if (score >= 40) return 'moderado';
  return 'baixo';
}
